import React from 'react';

export const ScoreTrendAnalysis = () => {
    return (
        <div className='card card--md card--radius-sm dashboard__stats_card__mbts__score_trend_analysis'>
            <p className='text-medium'>Score Trend Analysis</p>
            <p className='text-sm text-medium color--txt-muted mt-xs'>Organisational Score vs Benchmark</p>
            <div className="dashboard__stats_card__mbts__score_trend_analysis__content">
                <Chart />
            </div>
        </div>
    );
};

const Chart = () => {
    type Point = { x: number; y: number };

    const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const orgScores = [42, 36, 50, 46, 54, 58, 72, 66, 61, 69, 73, 68];
    const benchmarkScores = [45, 48, 50, 52, 54, 56, 58, 60, 62, 64, 66, 68];

    const width = 640;
    const height = 220;
    const padX = 24;
    const padY = 18;
    const plotW = width - padX * 2;
    const plotH = height - padY * 2;
    const baseY = padY + plotH;

    const allValues = [...orgScores, ...benchmarkScores];
    const minVal = Math.min(...allValues);
    const maxVal = Math.max(...allValues);
    const range = Math.max(1, maxVal - minVal);
    const minY = minVal - range * 0.12;
    const maxY = maxVal + range * 0.12;
    const yRange = Math.max(1, maxY - minY);

    const xStep = labels.length > 1 ? plotW / (labels.length - 1) : 0;

    const toPoints = (values: number[]): Point[] =>
        values.map((v, i) => ({
            x: padX + i * xStep,
            y: padY + ((maxY - v) / yRange) * plotH,
        }));

    const toSmoothPath = (pts: Point[]): string => {
        if (pts.length === 0) return '';
        if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;

        const d: string[] = [`M ${pts[0].x} ${pts[0].y}`];
        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[i - 1] ?? pts[i];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[i + 2] ?? p2;

            const c1x = p1.x + (p2.x - p0.x) / 6;
            const c1y = p1.y + (p2.y - p0.y) / 6;
            const c2x = p2.x - (p3.x - p1.x) / 6;
            const c2y = p2.y - (p3.y - p1.y) / 6;

            d.push(`C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`);
        }
        return d.join(' ');
    };

    const orgPts = toPoints(orgScores);
    const benchPts = toPoints(benchmarkScores);

    const orgPath = toSmoothPath(orgPts);
    const benchPath = toSmoothPath(benchPts);
    const orgAreaPath =
        orgPts.length === 0
            ? ''
            : `${orgPath} L ${orgPts[orgPts.length - 1].x} ${baseY} L ${orgPts[0].x} ${baseY} Z`;

    return (
        <div className="dashboard__stats_card__mbts__score_trend_analysis__chart_wrap" aria-hidden="true">
            <svg
                className="dashboard__stats_card__mbts__score_trend_analysis__chart"
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
            >
                <defs>
                    <linearGradient id="mbts-org-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#A855F7" stopOpacity="0.35" />
                        <stop offset="80%" stopColor="#A855F7" stopOpacity="0.06" />
                        <stop offset="100%" stopColor="#A855F7" stopOpacity="0" />
                    </linearGradient>
                </defs>

                <line x1={padX} y1={baseY} x2={width - padX} y2={baseY} stroke="#EEF2F7" strokeWidth="2" />
                <line
                    x1={padX}
                    y1={padY + plotH * 0.33}
                    x2={width - padX}
                    y2={padY + plotH * 0.33}
                    stroke="#F3F5FA"
                    strokeWidth="2"
                />
                <line
                    x1={padX}
                    y1={padY + plotH * 0.66}
                    x2={width - padX}
                    y2={padY + plotH * 0.66}
                    stroke="#F3F5FA"
                    strokeWidth="2"
                />

                {orgAreaPath ? <path d={orgAreaPath} fill="url(#mbts-org-fill)" /> : null}

                {benchPath ? (
                    <path
                        d={benchPath}
                        fill="none"
                        stroke="#C7CDD8"
                        strokeWidth="3"
                        strokeDasharray="10 10"
                        strokeLinecap="round"
                        opacity="0.9"
                    />
                ) : null}

                {orgPath ? (
                    <path
                        d={orgPath}
                        fill="none"
                        stroke="#A855F7"
                        strokeWidth="5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                ) : null}

                {orgPts.length ? (
                    <>
                        <circle
                            cx={orgPts[orgPts.length - 1].x}
                            cy={orgPts[orgPts.length - 1].y}
                            r="7"
                            fill="#FFFFFF"
                            stroke="#A855F7"
                            strokeWidth="4"
                        />
                        <circle
                            cx={orgPts[orgPts.length - 1].x}
                            cy={orgPts[orgPts.length - 1].y}
                            r="3.5"
                            fill="#A855F7"
                        />
                    </>
                ) : null}
            </svg>
        </div>
    );
};